import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStore } from '../../context/StoreContext';

// =============================================================================
// Keeps access and the open page in step with the selected branch.
//  - loads live access on login and on every branch change
//  - HQ selected: branch pages (bills, invoices, clients...) cannot stay open -> dashboard
//    (the wallet opens at HQ too: /wallet-billing is the HQ master wallet there)
//  - a branch selected: HQ-only pages (branch limits, activate stores, royalty) cannot stay open -> dashboard
//  - branch changed while a bill / client / booking of the old branch is open -> its list page
// Place inside <StoreProvider> (inside the router).
// A new HQ page must be added to HQ_PAGES (and to HQ_ONLY_PAGES if branches do not have it),
// or it sends the user back to the dashboard.
// =============================================================================
// Pages that open at HQ (some exist at a branch too, in their branch form)
const HQ_PAGES = ['/', '/settings', '/global-access', '/marketing', '/integrations', '/config', '/dev/activate-stores', '/access-control', '/unauthorized', '/wallet-billing', '/royalty', '/warehouse'];
// Pages this component never touches (Hirsta team page, public links, login)
const FREE_PAGES = ['/hirsta-access', '/login', '/i', '/closing-report', '/reports/daily'];
// Pages that exist ONLY at HQ
const HQ_ONLY_PAGES = ['/global-access', '/dev/activate-stores', '/access-control', '/royalty'];

// detail page of the old branch -> list page
const LIST_OF = [
  [/^\/create-bill(\/.*)?$/, '/create-bill'],
  [/^\/update-invoice\/.+$/, '/invoices'],
  [/^\/booking\/.+$/, '/booking-history'],
  [/^\/clients\/.+$/, '/clients'],
  [/^\/staff\/.+$/, '/staff'],
  [/^\/users\/.+$/, '/users'],
  [/^\/marketing\/campaigns\/.+$/, '/marketing/campaigns'],
  [/^\/marketing\/templates\/.+$/, '/marketing/templates'],
];

const matches = (list, path) => list.some((p) => (p === '/' ? path === '/' : path === p || path.startsWith(`${p}/`)));
const allowedAtHq = (path) => matches(HQ_PAGES, path);
const hqOnly = (path) => matches(HQ_ONLY_PAGES, path);
const free = (path) => matches(FREE_PAGES, path);

export default function AccessSync() {
  const { user, refreshAccess, refreshIfOld } = useAuth();
  const { activeStore } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const storeId = activeStore?._id || activeStore?.id || null;
  const isHq = Boolean(activeStore?.isHQ || storeId === 'global_hq');
  const userKey = user ? String(user.id || user._id || user.username || '') : '';
  const lastStore = useRef(null);

  // live access
  useEffect(() => {
    if (!userKey) return;
    refreshAccess(storeId && !isHq ? storeId : null);
  }, [userKey, storeId, isHq, refreshAccess]);

  // branch switch: leave pages that belong to the old branch
  useEffect(() => {
    if (!storeId) return;
    const prev = lastStore.current;
    lastStore.current = storeId;
    if (!prev || prev === storeId) return;
    const path = location.pathname;
    if (free(path)) return;
    if (isHq) { if (!allowedAtHq(path)) navigate('/', { replace: true }); return; }
    // HQ -> branch (or branch -> branch) while an HQ-only page is open: branch dashboard
    if (hqOnly(path)) { navigate('/', { replace: true }); return; }
    if (location.search.includes('branch=')) { navigate(location.pathname, { replace: true }); return; }
    const hit = LIST_OF.find(([rx]) => rx.test(path));
    if (hit && path !== hit[1]) navigate(hit[1], { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  // every page change: pick up access changes made meanwhile (at most every 15 s)
  useEffect(() => {
    if (userKey && refreshIfOld) refreshIfOld();
  }, [location.pathname, userKey, refreshIfOld]);

  // URL typed / bookmarked: HQ selected -> no branch pages; a branch selected -> no HQ-only pages
  useEffect(() => {
    if (!storeId || free(location.pathname)) return;
    if (isHq ? !allowedAtHq(location.pathname) : hqOnly(location.pathname)) navigate('/', { replace: true });
  }, [storeId, isHq, location.pathname, navigate]);

  return null;
}
